/**
 * PurchaseInvoicePrint — A4 print layout for Purchase Invoice / Returns
 * Triggered via Print button (F9) in PurchaseInvoiceV2Screen
 * Uses window.print() with @media print CSS
 */
import React from 'react';

function fmt(v, dec=2) { return (parseFloat(v)||0).toFixed(dec); }
function fmtIn(v) { return (parseFloat(v)||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }

export default function PurchaseInvoicePrint({ cfg, hdr, rows, bill, totals, company, onClose }) {
  const { grossAmt, discAmt, totGST, netAmt, cgstAmt, sgstAmt, freightGST } = totals;
  const freight = parseFloat(bill.freight)||0;
  const roundOff = parseFloat(bill.roundOff)||0;
  const itemRows = rows.filter(r => r.code || r.qty);

  return (
    <div className="pi2-print-overlay">
      <div className="pi2-print-actions no-print">
        <button onClick={() => window.print()} className="pi2-btn primary">🖨 Print</button>
        <button onClick={onClose} className="pi2-btn">✕ Close</button>
      </div>

      <div className="pi2-print-page">
        {/* Header */}
        <div className="pi2-print-header">
          <div className="pi2-print-co">
            <div className="pi2-print-co-name">{company?.name || 'Company Name'}</div>
            <div className="pi2-print-co-addr">{company?.address || ''}</div>
            <div className="pi2-print-co-gstin">GSTIN: {company?.gstin || '—'}</div>
          </div>
          <div className="pi2-print-doc-title">
            <div className="pi2-print-title">{cfg.title}</div>
            <div className="pi2-print-docno">
              {cfg.docLabel}: <strong>{hdr.docNo || '—'}</strong>
            </div>
          </div>
        </div>

        {/* Document info */}
        <div className="pi2-print-inforow">
          <div className="pi2-print-infobox">
            <table className="pi2-print-kv">
              <tbody>
                <tr><td>Date</td><td>{hdr.invDate || hdr.retDate || '—'}</td></tr>
                <tr><td>Supplier</td><td>{hdr.supplier || '—'}</td></tr>
                <tr><td>Pay Mode</td><td>{hdr.payMode || 'Credit'}</td></tr>
                {(hdr.againstRef) && <tr><td>{cfg.isReturn ? 'Against Invoice' : 'Against GRN'}</td><td>{hdr.againstRef}</td></tr>}
                {hdr.remarks && <tr><td>Remarks</td><td>{hdr.remarks}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Items table */}
        <table className="pi2-print-items">
          <thead>
            <tr>
              <th style={{width:30}}>Sl</th>
              <th style={{width:80}}>Item Code</th>
              <th>Item Name</th>
              <th style={{width:45}}>UOM</th>
              <th style={{width:50}}>Qty</th>
              <th style={{width:65}}>Rate</th>
              <th style={{width:55}}>Disc</th>
              <th style={{width:50}}>CGST%</th>
              <th style={{width:50}}>SGST%</th>
              <th style={{width:80}}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {itemRows.map((r, i) => (
              <tr key={r._id}>
                <td style={{textAlign:'center'}}>{i+1}</td>
                <td>{r.code}</td>
                <td>{r.name}</td>
                <td>{r.uom}</td>
                <td style={{textAlign:'right'}}>{fmt(r.qty)}</td>
                <td style={{textAlign:'right'}}>{fmt(r.rate)}</td>
                <td style={{textAlign:'right'}}>{fmt(r.disc)}</td>
                <td style={{textAlign:'right'}}>{r.cgst}</td>
                <td style={{textAlign:'right'}}>{r.sgst}</td>
                <td style={{textAlign:'right'}}>{fmtIn(r.amount)}</td>
              </tr>
            ))}
            {/* Pad to at least 5 rows for better layout */}
            {Array.from({length: Math.max(0, 5 - itemRows.length)}).map((_, i) => (
              <tr key={`pad-${i}`}><td colSpan={10}>&nbsp;</td></tr>
            ))}
          </tbody>
        </table>

        {/* Summary */}
        <div className="pi2-print-summary">
          <div className="pi2-print-tax">
            <table className="pi2-print-kv">
              <tbody>
                <tr><td>CGST</td><td className="num">₹ {fmtIn(cgstAmt)}</td></tr>
                <tr><td>SGST</td><td className="num">₹ {fmtIn(sgstAmt)}</td></tr>
                <tr><td>IGST</td><td className="num">₹ 0.00</td></tr>
                {freight > 0 && <tr><td>Freight</td><td className="num">₹ {fmtIn(freight)}</td></tr>}
                {bill.freightGst && freightGST > 0 && <tr><td>Freight GST</td><td className="num">₹ {fmtIn(freightGST)}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="pi2-print-amts">
            <table className="pi2-print-kv">
              <tbody>
                <tr><td>Gross Amount</td><td className="num">₹ {fmtIn(grossAmt)}</td></tr>
                {discAmt > 0 && <tr><td>Discount</td><td className="num">– ₹ {fmtIn(discAmt)}</td></tr>}
                <tr><td>GST Amount</td><td className="num">₹ {fmtIn(totGST)}</td></tr>
                {roundOff !== 0 && <tr><td>Round Off</td><td className="num">{roundOff > 0 ? '+' : ''}₹ {fmtIn(roundOff)}</td></tr>}
                <tr className="pi2-print-total"><td>Net Amount</td><td className="num">₹ {fmtIn(netAmt)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="pi2-print-footer">
          <div className="pi2-print-sign">
            <div className="pi2-print-sign-line">Authorised Signatory</div>
          </div>
          <div className="pi2-print-sign">
            <div className="pi2-print-sign-line">Receiver's Signature</div>
          </div>
        </div>

        <div className="pi2-print-note">
          This is a computer-generated document. Printed on {new Date().toLocaleString('en-IN')}.
        </div>
      </div>

      <style>{`
        .pi2-print-overlay {
          position: fixed; inset: 0; background: #f0f0f0;
          z-index: 9999; overflow: auto; padding: 20px;
        }
        .pi2-print-actions {
          display: flex; gap: 8px; justify-content: flex-end;
          margin-bottom: 12px; padding: 0 8px;
        }
        .pi2-print-page {
          width: 210mm; min-height: 297mm; background: #fff;
          margin: 0 auto; padding: 14mm 14mm 10mm;
          font-family: "Segoe UI", Arial, sans-serif; font-size: 11px;
          color: #1a1a1a; box-shadow: 0 2px 16px rgba(0,0,0,.12);
        }
        .pi2-print-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 2px solid #1856a7; padding-bottom: 8px; margin-bottom: 8px;
        }
        .pi2-print-co-name { font-size: 15px; font-weight: 700; color: #1856a7; }
        .pi2-print-co-addr, .pi2-print-co-gstin { font-size: 10px; color: #555; margin-top: 2px; }
        .pi2-print-doc-title { text-align: right; }
        .pi2-print-title { font-size: 14px; font-weight: 700; color: #1856a7; }
        .pi2-print-docno { font-size: 11px; margin-top: 3px; }
        .pi2-print-inforow { margin-bottom: 8px; }
        .pi2-print-infobox { display: inline-block; }
        .pi2-print-kv { border-collapse: collapse; font-size: 10.5px; }
        .pi2-print-kv td { padding: 2px 8px 2px 0; }
        .pi2-print-kv td:first-child { color: #555; min-width: 90px; }
        .pi2-print-kv .num { text-align: right; font-family: monospace; }
        .pi2-print-items {
          width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10.5px;
        }
        .pi2-print-items th {
          background: #dce8f7; border: 1px solid #b8cfe6;
          padding: 4px 5px; text-align: left; font-weight: 700; color: #0f3a66;
        }
        .pi2-print-items td {
          border: 1px solid #e0e8f0; padding: 3px 5px;
        }
        .pi2-print-items tr:nth-child(even) td { background: #f6f9fc; }
        .pi2-print-summary {
          display: flex; justify-content: flex-end; gap: 24px; margin-bottom: 16px;
        }
        .pi2-print-total td { font-weight: 700; font-size: 12px; border-top: 1px solid #555; padding-top: 4px; }
        .pi2-print-footer {
          display: flex; justify-content: space-between; margin-top: 24px;
          padding-top: 12px; border-top: 1px solid #ccc;
        }
        .pi2-print-sign { text-align: center; }
        .pi2-print-sign-line { border-top: 1px solid #555; margin-top: 32px; padding-top: 4px; font-size: 10px; color: #555; min-width: 140px; }
        .pi2-print-note { font-size: 9px; color: #999; text-align: center; margin-top: 10px; }
        @media print {
          .no-print { display: none !important; }
          .pi2-print-overlay { position: static; background: none; padding: 0; overflow: visible; }
          .pi2-print-page { box-shadow: none; margin: 0; width: 100%; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
}
